import React, { FC, useMemo, useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import { ReactComponent as ArrowIcon } from '@blink/assets/src/icons/arrow.svg';
import { ReactComponent as CloseIcon } from '@blink/assets/src/icons/close.svg';
import { Controller, useFormContext } from 'react-hook-form';

import { AssetItem } from '../AssetItem/AssetItem';
import { AssetSectionFields } from '../AssetSection';
import { useAssetCardStyle } from './AssetCard.style';

import { RiskFactorFields } from '@/features/Modals/ui/BorrowModal/fields';
import { SelectedBorrowAsset } from '@/features/Modals/ui/BorrowModal';
import { formatWithCommas, formatWithMaxPrecision } from '@/shared/utils/helpers';
import { LeverageStrategy } from '@/shared/validations/borrowModal.schema';
import { Agreement, Token } from '@/shared/api/borrow';
import { NumericInput } from '@/widgets/NumericInput';
import { AssetWithIcon } from '@/shared/ui';

type CollateralAssetSelectedCardProps = {
    asset: Omit<SelectedBorrowAsset, 'amount'>;
    agreement: Agreement;
    index: number;
    assetAmount?: string;
    onDelete: () => void;
    onUpdate: (asset: Partial<SelectedBorrowAsset>) => void;
    assetList: Token[];
    disabled?: boolean;
    decimals: number;
    selectedAssets: SelectedBorrowAsset[];
    maxBalance?: string;
    assetSymbol?: string;
};

export enum CollateralAssetSelectedFields {
    AMOUNT = 'amount',
}

export const AssetCard: FC<CollateralAssetSelectedCardProps> = ({
    asset,
    agreement,
    assetList,
    index,
    disabled,
    onDelete,
    onUpdate,
    selectedAssets,
    maxBalance,
    decimals,
    assetSymbol,
}) => {
    const classes = useAssetCardStyle();
    const { control, setValue, trigger, getFieldState } = useFormContext();

    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const toggleOpen = () => setOpen((prevOpen) => !prevOpen);

    const handleSelectedAsset = async (symbol: string) => {
        const asset = assetList.find((asset) => asset.symbol === symbol);

        onUpdate({
            ...asset,
            amount: asset!.balance,
            maxBalance: asset!.balance,
        });

        await trigger(
            `${AssetSectionFields.SELECTED_ASSETS}[${index}].${CollateralAssetSelectedFields.AMOUNT}`,
        );

        setOpen(false);
    };

    const handleMaxButtonClick = async () => {
        setValue(
            `${AssetSectionFields.SELECTED_ASSETS}[${index}].${CollateralAssetSelectedFields.AMOUNT}`,
            maxBalance,
        );

        onUpdate({
            amount: maxBalance,
        });

        await trigger(
            `${AssetSectionFields.SELECTED_ASSETS}[${index}].${CollateralAssetSelectedFields.AMOUNT}`,
        );
    };

    const formatMaxBalance = useMemo(() => {
        const plainDecimal = Number(maxBalance).toFixed(decimals);

        const isNaN = Number.isNaN(Number(plainDecimal));

        return formatWithMaxPrecision(isNaN ? '0' : plainDecimal || '0', decimals);
    }, [maxBalance, decimals]);

    const { invalid } = getFieldState(`${AssetSectionFields.SELECTED_ASSETS}[${index}]`);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const targetElement = event.target as Element | null;

            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                !targetElement?.closest?.('.dropdown-toggler')
            ) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={classes.relative}>
            <div
                className={cn(classes.root, {
                    [classes.invalid]: invalid,
                })}
            >
                {index !== 0 && (
                    <div className={classes.closeBtn} onClick={onDelete}>
                        <CloseIcon />
                    </div>
                )}
                <div className={classes.column}>
                    <div className={classes.collateralWrapper}>
                        <p>Collateral</p>
                        <p className={classes.row}>
                            Wallet balance: {formatWithCommas(formatMaxBalance)}{' '}
                            <span className={classes.buttonMax} onClick={handleMaxButtonClick}>
                                Max
                            </span>
                        </p>
                    </div>

                    <div className={classes.amountWrapper}>
                        <div className={classes.amount}>
                            <Controller
                                name={`${AssetSectionFields.SELECTED_ASSETS}[${index}].${CollateralAssetSelectedFields.AMOUNT}`}
                                control={control}
                                render={({ field }) => {
                                    const handleInputChange = async (value: string) => {
                                        const num1Decimals = value.split('.')[1] ?? '';

                                        setValue(
                                            RiskFactorFields.STRATEGY,
                                            LeverageStrategy.MANUAL,
                                        );

                                        if (!num1Decimals || num1Decimals.length <= decimals) {
                                            field.onChange(value);
                                            await trigger(field.name);
                                        }
                                    };

                                    return (
                                        <>
                                            <NumericInput
                                                value={field.value}
                                                onRawValueChange={handleInputChange}
                                                decimals={decimals}
                                                disabled={disabled}
                                                name={field.name}
                                                className={'pl-0'}
                                            />
                                        </>
                                    );
                                }}
                            />
                        </div>
                        <div
                            onClick={(event) => {
                                event.stopPropagation();

                                if (assetList.length) {
                                    toggleOpen();
                                }
                            }}
                            className={cn(classes.row, 'dropdown-toggler', {
                                [classes.disabled]: assetList.length === 0,
                            })}
                        >
                            <AssetWithIcon
                                assetName={asset.symbol || ''}
                                assetSymbol={asset.symbol || ''}
                                className={classes.assetWithIcon}
                            />
                            <div
                                style={{
                                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s ease',
                                }}
                            >
                                <ArrowIcon />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {open && (
                <div
                    ref={dropdownRef}
                    className={classes.assetsWrapper}
                    onClick={(event) => event.stopPropagation()}
                >
                    {assetList.map((asset) => (
                        <AssetItem
                            key={asset.id}
                            assetToBorrowName={asset.name}
                            amount={asset.balance || ''}
                            name={asset.name}
                            address={asset.address || ''}
                            selectedInfo={selectedAssets.find(
                                (selectedAsset) => selectedAsset.name === asset.name,
                            )}
                            onSelect={handleSelectedAsset}
                            disabled={disabled}
                            symbol={asset.symbol}
                            decimals={agreement.leverage.decimals || 0}
                            assetSymbol={assetSymbol}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
